import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, LogOut, LogIn, BookOpen, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useBook } from '../context/BookContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [bookDropdownOpen, setBookDropdownOpen] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { books, selectedBook, selectBook } = useBook();

  const navigation = [
    { name: 'Treasure Map', href: '/' },
    { name: 'Library', href: '/library' },
    { name: 'Mind Map', href: '/mindmap' },
    { name: 'GIT Investigator', href: '/git-investigator' },
    { name: 'Buy', href: '/buy' },
    { name: 'Subscriptions', href: '/subscriptions' },
    { name: 'Kingdom Escort Guard', href: '/kingdom-guard' },
    { name: 'Leaderboard', href: '/leaderboard' },
    { name: 'Streaks', href: '/streaks' },
    { name: 'About', href: '/about' }
  ];

  const isActive = (href) => location.pathname === href;
  const cartCount = getCartCount();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="text-2xl">📯</div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900">Trump of the Lord</span>
              <span className="text-xs text-gray-600 -mt-1">Ministries</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Book Selector - visible when multiple books */}
          {books.length > 1 && (
            <div className="hidden lg:block relative" data-testid="book-selector">
              <button
                onClick={() => setBookDropdownOpen(!bookDropdownOpen)}
                onBlur={() => setTimeout(() => setBookDropdownOpen(false), 200)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors max-w-[200px]"
                data-testid="book-selector-trigger"
              >
                <BookOpen className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{selectedBook?.title || 'Select Book'}</span>
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${bookDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {bookDropdownOpen && (
                <div className="absolute top-full mt-1 right-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Reading
                  </div>
                  {books.map(book => (
                    <button
                      key={book.book_id}
                      onClick={() => { selectBook(book.book_id); setBookDropdownOpen(false); }}
                      data-testid={`book-option-${book.book_id}`}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                        selectedBook?.book_id === book.book_id
                          ? 'bg-amber-50 text-amber-900 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <BookOpen className="h-4 w-4 shrink-0 text-amber-600" />
                      <div className="min-w-0">
                        <div className="truncate">{book.title}</div>
                        {book.author && <div className="text-xs text-gray-400 truncate">{book.author}</div>}
                      </div>
                      {selectedBook?.book_id === book.book_id && (
                        <span className="ml-auto text-amber-600 text-xs font-bold">Active</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Right Actions */}
          <div className="flex items-center space-x-2">
            {/* Cart Icon - Always Visible */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => window.location.href = '/cart'}
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-amber-600 text-white text-xs">
                  {cartCount}
                </Badge>
              )}
            </Button>
            
            {isAuthenticated() ? (
              <>
                {/* Admin Link - Only for admin email */}
                {(user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com') && (
                  <Button 
                    variant="ghost"
                    className="hidden sm:flex"
                    onClick={() => window.location.href = '/admin'}
                  >
                    📚 Admin
                  </Button>
                )}
                
                {/* Account Icon - Always Visible when logged in */}
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => window.location.href = '/account'}
                  title="My Account"
                >
                  <User className="h-5 w-5" />
                </Button>
                {/* Logout Icon - Visible on desktop */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="hidden sm:flex"
                  onClick={logout}
                  title="Sign Out"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <>
                {/* Login Icon - Always Visible when not logged in */}
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => window.location.href = '/login'}
                  title="Sign In"
                >
                  <LogIn className="h-5 w-5" />
                </Button>
              </>
            )}
            
            {/* Get Started Button - Desktop only */}
            <Button 
              className="hidden sm:flex"
              onClick={() => window.location.href = '/subscriptions'}
            >
              Get Started
            </Button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-1 border-t">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-2 rounded-md text-base font-medium ${
                  isActive(item.href)
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link to="/cart" onClick={() => setIsMenuOpen(false)}>
              <div className="flex items-center justify-between px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">
                <span>Cart</span>
                {cartCount > 0 && (
                  <Badge className="bg-amber-600 text-white">{cartCount}</Badge>
                )}
              </div>
            </Link>
            <div className="pt-4 space-y-2 border-t">
              {/* Mobile Book Selector */}
              {books.length > 1 && (
                <div className="px-4 pb-3 border-b mb-2">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Active Book</label>
                  <select
                    value={selectedBook?.book_id || ''}
                    onChange={(e) => { selectBook(e.target.value); setIsMenuOpen(false); }}
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm bg-amber-50 text-amber-900"
                    data-testid="book-selector-mobile"
                  >
                    {books.map(b => (
                      <option key={b.book_id} value={b.book_id}>{b.title}</option>
                    ))}
                  </select>
                </div>
              )}
              {isAuthenticated() ? (
                <>
                  <Link to="/account" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      <User className="h-4 w-4 mr-2" />
                      My Account
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start"
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                </Link>
              )}
              <Link to="/subscriptions">
                <Button className="w-full">Get Started</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
